// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: how conversations are laid out, chosen in Appearance and kept per device.
// Bubbles (the default since 2026-09) puts your messages on the right in bubbles;
// Classic keeps the flat, everyone-on-the-left rows.

import {makePersistent} from '@app/features/platform/utils/MobXPersistence';
import {autorun, makeAutoObservable, runInAction} from 'mobx';

export const MessageStyles = {
	BUBBLES: 'bubbles',
	CLASSIC: 'classic',
} as const;
export type MessageStyle = (typeof MessageStyles)[keyof typeof MessageStyles];

const STYLE_VALUES = new Set<string>(Object.values(MessageStyles));
/** Class on <html> while bubbles are on, so plain CSS can switch layouts. */
export const MESSAGE_BUBBLES_CLASS = 'message-bubbles';

class MessageStylePreferenceStore {
	style: MessageStyle = MessageStyles.BUBBLES;
	private _hydrated = false;

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
		void this.initPersistence();
		if (typeof document !== 'undefined') {
			autorun(() => {
				document.documentElement.classList.toggle(MESSAGE_BUBBLES_CLASS, this.style === MessageStyles.BUBBLES);
			});
		}
	}

	get hydrated(): boolean {
		return this._hydrated;
	}

	get bubbles(): boolean {
		return this.style === MessageStyles.BUBBLES;
	}

	setStyle(style: MessageStyle): void {
		if (STYLE_VALUES.has(style)) this.style = style;
	}

	private async initPersistence(): Promise<void> {
		await makePersistent(this, 'MessageStylePreference', ['style'], {syncAcrossTabs: true});
		runInAction(() => {
			if (!STYLE_VALUES.has(this.style)) this.style = MessageStyles.BUBBLES;
			this._hydrated = true;
		});
	}
}

export default new MessageStylePreferenceStore();
